/**
 * PathwayModeBanner (Sprint 4.3.6).
 *
 * Founder feedback (2026-05-08 00:05): the editor "is parked at the
 * bottom of long form where I don't get the logic of how it is designed
 * or what is the difference between editor and runtime." The Mode toggle
 * pill is too small to communicate the semantic shift between Design
 * Mode (authoring the template) and Runtime Mode (viewing an enrolled
 * patient's live activities).
 *
 * This banner sits directly above the canvas (between
 * <PathwayCanvasToolbar> and the canvas surface) and surfaces in plain
 * language what each mode does:
 *   - Design Mode  : "Authoring the {clinicalPathwayName} template."
 *                    Caption: "Your edits become the next published
 *                    version. Drag from the palette to add nodes;
 *                    click any node to edit."
 *   - Runtime Mode : "Viewing {patientName}'s active activities."
 *                    Caption: "Read-only on action defs. Click any
 *                    action node for the activity inspector. Patient-
 *                    pathway-timeline below."
 *
 * Brand-Patch v2 mandatory: pathway display names come from the canonical
 * lookup map in `<AppShell>` (PATHWAY_DISPLAY_NAMES). NEVER render
 * `\bTLP[1-4]\b` user-facing strings. Internal version-ids are preserved
 * per HC9 only in wire format (audit emit), not banner copy.
 *
 * Persistence: a click on the X dismiss button writes
 * `cps:pathway-editor:banner-dismissed-{mode}` to localStorage. A subsequent
 * mode switch re-shows the banner for the OTHER mode (only the matching
 * key is read), so the user opts back in by switching modes. This
 * matches the founder direction: the banner is an onboarding scaffold,
 * not a permanent fixture.
 *
 * HC2 token-only (signal-primary-* + status-active-* tiers). HC5 zero em
 * / en dashes. HC6 a11y: `role="status"` + `aria-live="polite"` so
 * screen readers announce the banner contents on first render and after
 * a mode switch; the dismiss button has a meaningful `aria-label`.
 *
 * Render contract:
 *   <div role="status" aria-live="polite"
 *        data-testid="pathway-mode-banner" data-mode="design|runtime">
 *     <icon /> <h3>{heading}</h3> <p>{caption}</p>
 *     <button aria-label="Dismiss banner">X</button>
 *   </div>
 */
import { useCallback, useEffect, useState } from 'react';
import type { PathwayEditorMode } from '../../state/pathway-editor-mode.store';

export const PATHWAY_BANNER_DISMISS_KEY_PREFIX =
  'cps:pathway-editor:banner-dismissed-';

export function pathwayBannerDismissKey(mode: PathwayEditorMode): string {
  return `${PATHWAY_BANNER_DISMISS_KEY_PREFIX}${mode}`;
}

function readBannerDismissed(mode: PathwayEditorMode): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(pathwayBannerDismissKey(mode)) === 'true';
  } catch {
    return false;
  }
}

function persistBannerDismissed(mode: PathwayEditorMode): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(pathwayBannerDismissKey(mode), 'true');
  } catch (err) {
    console.warn(
      '[pathway-mode-banner] dismiss persist failed; will re-show on next render',
      err,
    );
  }
}

export interface PathwayModeBannerProps {
  /** Active editor mode. Drives copy + colour. */
  mode: PathwayEditorMode;
  /**
   * Display name of the clinical pathway (Design Mode heading slot).
   * Resolved via `PATHWAY_DISPLAY_NAMES` (canonical lookup at
   * `src/layouts/AppShell.tsx:25`) per Brand-Patch v2 - NEVER pass a
   * `TLP[1-4]` string here.
   */
  clinicalPathwayName: string;
  /**
   * Patient name surfaced in the Runtime Mode heading slot. May be
   * `null` when no patient is enrolled - the banner falls back to a
   * generic "the active patient" placeholder so the heading still
   * carries meaning. The empty-state card (rendered separately by
   * <PathwayEditor>) is the action surface for "switch to Design Mode".
   */
  patientName: string | null;
}

/** Inline pencil icon. HC2 token: `currentColor` only. */
function PencilIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-5 w-5 shrink-0"
    >
      <path
        d="M14.06 4.94l3 3L7.5 17.5l-4 1 1-4 9.56-9.56z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 5l5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Inline play / activity pulse icon for Runtime. */
function ActivityIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-5 w-5 shrink-0"
    >
      <path
        d="M3 12h4l2-6 4 12 2-6h6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Inline X / close icon. */
function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path
        d="M6 6l12 12M18 6L6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PathwayModeBanner({
  mode,
  clinicalPathwayName,
  patientName,
}: PathwayModeBannerProps) {
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readBannerDismissed(mode),
  );

  // Re-evaluate dismiss state when the mode flips. Each mode tracks its
  // own dismiss key so flipping from Design (dismissed) to Runtime
  // re-shows the Runtime banner if the user has not dismissed that one.
  useEffect(() => {
    setDismissed(readBannerDismissed(mode));
  }, [mode]);

  const handleDismiss = useCallback(() => {
    persistBannerDismissed(mode);
    setDismissed(true);
  }, [mode]);

  if (dismissed) return null;

  const isDesign = mode === 'design';
  // U+00B7 middle dot per HC5 separator convention (AppShell breadcrumb).
  const heading = isDesign
    ? `DESIGN · Authoring the ${clinicalPathwayName} template`
    : `RUNTIME · Viewing ${patientName ?? 'the active patient'}'s active activities`;
  const caption = isDesign
    ? 'Your edits become the next published version. Drag from the palette to add nodes; click any node to edit.'
    : "Read-only on action defs. Click any action node for the activity inspector. Patient pathway timeline below.";

  // Colour-coded surface. Both tiers exist in `src/styles/tokens.ts`.
  const wrapperClass = isDesign
    ? 'border-l-4 border-signal-primary-500 bg-signal-primary-50 text-text-primary'
    : 'border-l-4 border-status-active bg-status-active-bg text-status-active-fg';
  const iconWrapClass = isDesign ? 'text-signal-primary-600' : 'text-status-active';
  const dismissClass = isDesign
    ? 'text-signal-primary-600 hover:text-signal-primary-800 focus:ring-signal-primary-500'
    : 'text-status-active hover:text-status-active-fg focus:ring-status-active';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="pathway-mode-banner"
      data-mode={mode}
      className={[
        'flex items-start gap-gutter-md px-gutter-lg py-gutter-sm',
        wrapperClass,
      ].join(' ')}
    >
      <span className={iconWrapClass} aria-hidden="true">
        {isDesign ? <PencilIcon /> : <ActivityIcon />}
      </span>
      <div className="min-w-0 flex-1">
        <p
          data-testid="pathway-mode-banner-heading"
          className="text-signal-sm font-semibold tracking-tight"
        >
          {heading}
        </p>
        <p
          data-testid="pathway-mode-banner-caption"
          className="mt-0.5 text-signal-xs text-text-secondary"
        >
          {caption}
        </p>
      </div>
      <button
        type="button"
        data-testid="pathway-mode-banner-dismiss"
        aria-label="Dismiss mode banner"
        onClick={handleDismiss}
        className={[
          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-signal-sm',
          'focus:outline-none focus:ring-2',
          dismissClass,
        ].join(' ')}
      >
        <CloseIcon />
      </button>
    </div>
  );
}
